import {
  AfterViewInit,
  ChangeDetectionStrategy,
  ChangeDetectorRef,
  Component,
  ContentChildren,
  EventEmitter,
  Input,
  OnChanges,
  OnInit,
  Output,
  QueryList,
  SimpleChanges,
  TemplateRef
} from '@angular/core';
import {NzSafeAny} from 'ng-zorro-antd/core/types';
import {NzTableQueryParams, NzTableSize} from 'ng-zorro-antd/table';
import {NzResizeEvent} from 'ng-zorro-antd/resizable';
import {InputBoolean} from 'ng-zorro-antd/core/util';
import {AppTableComponentToken, AppTableConfig, AppTableHeader} from './app-table.interfaces';
import {NgTemplateNameDirective} from '@shared/directives/ng-template-ref-name.directive';

@Component({
  selector: 'app-table',
  templateUrl: './app-table.component.html',
  styleUrls: ['./app-table.component.scss'],
  providers: [
    { provide: AppTableComponentToken, useExisting: AppTableComponent }
  ],
  changeDetection: ChangeDetectionStrategy.OnPush
})
export class AppTableComponent implements OnInit, OnChanges, AfterViewInit {
  readonly DEFAULT_CHANGE_SIZE_OPTIONS = [10,50,100]
  _dataList!: NzSafeAny[];
  _tableSize: NzTableSize = 'default';

  @Input() tableConfig!: AppTableConfig;
  @Input() rangeTotalTemplate: TemplateRef<any>;
  @Output() onClickNode: EventEmitter<any> = new EventEmitter<any>();
  @Output() onClickExpand: EventEmitter<{data: any, isOpen: boolean}> = new EventEmitter<{data: any, isOpen: boolean}>();
  // Nếu checkbox = true
  @Input() checkedCashArrayFromComment: NzSafeAny[] = [];
  @Input() attrCheck: string = 'id';

  // SORT
  @Output() doSort: EventEmitter<{sortBy: string, ascOrDesc: string}> = new EventEmitter<{sortBy: string, ascOrDesc: string}>();


  @Input() changeDetect:boolean;

  @Input()
  set tableData(value: NzSafeAny[]) {
    this._dataList = value;
    if (this.tableConfig.showCheckbox) {
      this._dataList.forEach((item) => {
        item['_checked'] = false;
      });
    }
  }

  get tableData(): NzSafeAny[] {
    return this._dataList;
  }

  @Output() changePageNum = new EventEmitter<number>();
  @Output() changePageSize = new EventEmitter<number>();
  @Output() selectedChange: EventEmitter<NzSafeAny[]> = new EventEmitter<NzSafeAny[]>();
  // @Output() sortFn: EventEmitter<SortFile> = new EventEmitter<SortFile>();
  @Input() @InputBoolean() showSizeChanger = false;
  @Input() @InputBoolean() showQuickJumper = false;
  indeterminate: boolean = false;
  allChecked: boolean = false;
  rowSelected: any;

  expandSet = new Set<string>();

  onExpandChange(row: any, checked: boolean): void {
    if (checked) {
      this.expandSet = new Set<string>([row[this.attrCheck]]);
      this.onClickExpand.emit({data: row, isOpen: true});
    } else {
      this.expandSet = new Set<string>();
      this.onClickExpand.emit({data: row, isOpen:false});
    }
  }
  @ContentChildren(NgTemplateNameDirective) private _templates: QueryList<NgTemplateNameDirective>;

  constructor(private cdr: ChangeDetectorRef) {
  }

  ngOnInit(): void {
  }

  ngOnChanges(changes: SimpleChanges): void {
    if (this.tableConfig.showCheckbox) {
      this.refreshStatus();
    }
    this.cdr.markForCheck();
  }

  tableChangeDetection(): void {
    this._dataList = [...this._dataList??[]];
    this.cdr.markForCheck();
  }

  refreshStatus(): void {
    this._dataList.forEach((item) => {
      const index = this.checkedCashArrayFromComment.findIndex((cashItem) => {
        return item[this.attrCheck] === cashItem[this.attrCheck];
      });
      if (index !== -1) {
        item['_checked'] = true;
      } else {
        item['_checked'] = false;
      }
    });
    const allChecked = this._dataList.length > 0 && this._dataList.every((item) => {
      return item['_checked'] === true;
    });
    const allUnChecked = this._dataList.every(item => item['_checked'] !== true);
    this.allChecked = allChecked;
    this.indeterminate = !allChecked && !allUnChecked;
  }

  checkFn(dataItem: NzSafeAny, isChecked: boolean): void {
    dataItem['_checked'] = isChecked;
    const index = this.checkedCashArrayFromComment.findIndex((cashItem) => cashItem[this.attrCheck] === dataItem[this.attrCheck]);
    if (isChecked) {
      if (index === -1) {
        this.checkedCashArrayFromComment.push(dataItem);
      }
    } else {
      if (index !== -1) {
        this.checkedCashArrayFromComment.splice(index, 1);
      }
    }
  }

  onAllChecked(isChecked: boolean): void {
    this._dataList.forEach((item) => {
      this.checkFn(item, isChecked);
    });
    this.selectedChange.emit(this.checkedCashArrayFromComment);
    this.refreshStatus();
  }

  public checkRowSingle(isChecked: boolean, selectIndex: number): void {
    this.checkFn(this._dataList[selectIndex], isChecked);
    this.selectedChange.emit(this.checkedCashArrayFromComment);
    this.refreshStatus();
  }

  onResize({ width }: NzResizeEvent, col: string): void {
    this.tableConfig.headers = (this.tableConfig.headers.map(e => (e.title === col ? {
      ...e,
      width: +`${width}`
    } : e))) as AppTableHeader[];
  }

  onPageSizeChange($event: number): void {
    this.rowSelected = null;
    this.onClickNode.emit(null);
    this.changePageSize.emit($event);
  }

  onPageIndexChange($event: number): void {
    this.rowSelected = null;
    this.onClickNode.emit(null);
    this.changePageNum.emit($event);
  }

  // onQueryParamsChange(tableQueryParams: NzTableQueryParams): void {
  //   this.changePageNum.emit(tableQueryParams.pageSize);
  // }

  public trackByTableHead(index: number, item: NzSafeAny): NzSafeAny {
    return item;
  }

  public trackByTableBody(index: number, item: NzSafeAny): NzSafeAny {
    return item;
  }

  // Sort
  // changeSort(tableHeader: MBTableHeader): void {
  //   this.tableConfig.headers.forEach(item => {
  //     if (item.field !== tableHeader.field) {
  //       item.sortDir = undefined;
  //     }
  //   })
  //   const sortDicArray: [undefined, 'asc', 'desc'] = [undefined, 'asc', 'desc'];
  //   const index = sortDicArray.findIndex((item) => item === tableHeader.sortDir);
  //   tableHeader.sortDir = (index === sortDicArray.length - 1) ? sortDicArray[0] : sortDicArray[index + 1];
  //   this.sortFn.emit({fileName: tableHeader.field!, sortDir: tableHeader.sortDir})
  // }
  doClickItem(row: any) {
    if (this.tableConfig.activeRow) {
      this.rowSelected = row;
    }
    this.onClickNode.emit(row);
  }

  getData(row: any) {
    return Object.assign({}, {data: row}, row);
  }

  getTemplateRef(template: any ){
    if (template instanceof  TemplateRef) return template;
    if (typeof template === "string") return this._templates.find(item => item.name === template)?.template;
    return undefined
  }

  ngAfterViewInit(): void {
    // console.log(this._templates)
  }

  getRowIndex(index) {
    return index + 1 + this.tableConfig.pageSize * (this.tableConfig.pageIndex - 1);
  }

  onSort(params: NzTableQueryParams) {
    if (!this.tableConfig.showFrontPagination) {
      const {pageSize, pageIndex, sort, filter} = params
      const currentSort = sort.find(item => item.value !== null);
      const sortField = (currentSort && currentSort.key) || null;
      const sortOrder = (currentSort && currentSort.value) || null;
      this.doSort.emit(sortField != null ? {sortBy: sortField, ascOrDesc: sortOrder} : null)
    }

  }
}
